import { roundMoney, type MoneyScale } from '@shared/money';
import type { TextInputProps } from 'react-native';

/**
 * Parseo y validación de números ingresados por teclado (convención es-AR / Sudamérica).
 * - Coma o punto = separador decimal (1500,50 · 1500.50)
 * - Con coma: punto = separador de miles (1.500,50)
 * - Sin coma: un solo punto decimal (1500.50); 1.500 sin coma = 1,5
 */

export type ParseDecimalInputOptions = {
  /** Solo enteros; rechaza parte decimal. */
  integerOnly?: boolean;
  allowNegative?: boolean;
  min?: number;
  max?: number;
  /**
   * Monto de dinero es-AR: además de las reglas generales, puntos seguidos de
   * grupos de exactamente 3 dígitos se toman como separador de miles
   * ("76.033" → 76033, "1.500.000" → 1500000). Un punto con 1-2 decimales
   * sigue siendo decimal ("1500.50" → 1500.50). Usar en campos de plata
   * (contado, egresos, montos de pago); no en cantidades ni porcentajes.
   */
  money?: boolean;
  /** Tras parsear, redondea al exponente de la moneda (cobro / montos de org). */
  moneyScale?: MoneyScale;
};

export type ValidateDecimalFieldOptions = ParseDecimalInputOptions & {
  required?: boolean;
  requiredMessage?: string;
  invalidMessage?: string;
  minMessage?: string;
  maxMessage?: string;
  integerOnlyMessage?: string;
};

export const DECIMAL_HINT_PRICE = 'Monto con coma o punto decimal (ej. 1500,50 o 1500.50).';
export const DECIMAL_HINT_QTY = 'Número entero ≥ 0.';
export const DECIMAL_HINT_PERCENT = 'Número con coma o punto (ej. 10 o -15,5).';

/** Normaliza texto antes de parsear: quita $ y espacios. */
export function normalizeDecimalInputRaw(raw: string): string {
  return raw.trim().replace(/\$/g, '').replace(/\s/g, '');
}

/**
 * El texto crudo tiene un separador decimal explícito (`,` o `.`).
 * Útil para avisar cuando un campo entero lo va a descartar al sanitizar
 * (ej. "1,5" → "15" en kind='integer'). Mismo criterio que
 * inventory.isIntegerUnitDecimalAttempt.
 */
export function inputHasDecimalSeparator(raw: string): boolean {
  return /[.,]/.test(raw);
}

/** Formato editable es-AR (miles con punto, decimal con coma). Sin símbolo $ — el prefijo va en UI. */
export function formatEditableMoneyAmount(amount: number, locale = 'es-AR'): string {
  if (!Number.isFinite(amount)) return '';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(amount);
}

/**
 * Parsea entrada de usuario a número finito o null si no se entiende.
 * Misma regla que el histórico parseCustomerAmountInput.
 */
export function parseDecimalInput(
  raw: string,
  opts?: ParseDecimalInputOptions
): number | null {
  let normalized = normalizeDecimalInputRaw(raw);
  if (!normalized) return null;

  if (opts?.money && /^-?[1-9]\d{0,2}(\.\d{3})+$/.test(normalized)) {
    normalized = normalized.replace(/\./g, '');
  }

  if (normalized.includes(',')) {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  } else {
    normalized = normalized.replace(',', '.');
  }

  if (normalized.includes('-') && normalized !== normalized.replace(/-/g, '')) {
    const minusCount = (normalized.match(/-/g) ?? []).length;
    if (minusCount > 1 || (minusCount === 1 && !normalized.startsWith('-'))) {
      return null;
    }
  }

  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;

  if (!opts?.allowNegative && value < 0) return null;

  if (opts?.integerOnly) {
    if (!Number.isInteger(value) && Math.trunc(value) !== value) {
      const frac = Math.abs(value % 1);
      if (frac > 1e-9) return null;
    }
  }

  const finalValue = opts?.integerOnly ? Math.trunc(value) : value;

  if (opts?.min != null && finalValue < opts.min) return null;
  if (opts?.max != null && finalValue > opts.max) return null;

  if (opts?.moneyScale) {
    return roundMoney(finalValue, opts.moneyScale);
  }

  return finalValue;
}

export function validateDecimalField(
  raw: string,
  opts?: ValidateDecimalFieldOptions
): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return opts?.required
      ? (opts.requiredMessage ?? 'Este campo es obligatorio.')
      : null;
  }

  const value = parseDecimalInput(raw, opts);
  if (value === null) {
    return opts?.invalidMessage ?? 'Ingresá un número válido (ej. 1500 o 1500,50).';
  }

  if (opts?.integerOnly && !Number.isInteger(value)) {
    return opts.integerOnlyMessage ?? 'Ingresá un número entero.';
  }

  if (opts?.min != null && value < opts.min) {
    return opts.minMessage ?? `El valor debe ser al menos ${opts.min}.`;
  }

  if (opts?.max != null && value > opts.max) {
    return opts.maxMessage ?? `El valor no puede superar ${opts.max}.`;
  }

  return null;
}

/** Mensaje de error para precio > 0 (catálogo). */
export function validatePositivePriceField(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return 'Falta el precio.';
  const value = parseDecimalInput(raw, { min: 0 });
  if (value === null) return 'Ingresá un precio válido (ej. 1500 o 1500,50).';
  if (value <= 0) return 'Ingresá un precio mayor a cero.';
  return null;
}

/** Porcentaje (puede ser negativo); vacío = null parse para validación externa. */
export function parsePercentInput(raw: string): number | null {
  return parseDecimalInput(raw, { allowNegative: true });
}

/** Tipo de campo numérico para filtrado en tiempo real y teclado. */
export type NumericInputKind =
  | 'decimalMoney'
  | 'integer'
  | 'signedInteger'
  | 'signedDecimal'
  | 'percent'
  | 'digits'
  | 'phone';

function normalizeLeadingMinus(value: string): string {
  const negative = value.startsWith('-');
  const digits = value.replace(/-/g, '');
  return negative ? `-${digits}` : digits;
}

/** Quita ceros a la izquierda del entero ("010000" → "10000"); conserva "0", "0," y "0.". */
function stripRedundantLeadingZeros(value: string): string {
  return value.replace(/^(\$?\s*-?)0+(?=\d)/, '$1');
}

function sanitizeDecimalMoneyInput(raw: string): string {
  let s = raw.replace(/[^0-9,.\s$]/g, '');

  const commaIdx = s.indexOf(',');
  if (commaIdx !== -1) {
    const before = s.slice(0, commaIdx + 1);
    const after = s.slice(commaIdx + 1).replace(/,/g, '');
    s = before + after;
    s = s.replace(/\.{2,}/g, '.');
    return stripRedundantLeadingZeros(s);
  }

  const dotIdx = s.indexOf('.');
  if (dotIdx !== -1) {
    const before = s.slice(0, dotIdx + 1);
    const after = s.slice(dotIdx + 1).replace(/\./g, '');
    s = before + after;
  }

  return stripRedundantLeadingZeros(s);
}

function sanitizeSignedDecimalInput(raw: string): string {
  const negative = raw.trimStart().startsWith('-');
  const body = sanitizeDecimalMoneyInput(raw.replace(/-/g, ''));
  return negative ? `-${body}` : body;
}

/** Filtra caracteres no permitidos mientras el usuario escribe o pega (web y móvil). */
export function sanitizeNumericInputText(next: string, kind: NumericInputKind): string {
  switch (kind) {
    case 'decimalMoney':
      return sanitizeDecimalMoneyInput(next);
    case 'integer':
    case 'digits':
      return stripRedundantLeadingZeros(next.replace(/[^0-9]/g, ''));
    case 'signedInteger':
      return stripRedundantLeadingZeros(normalizeLeadingMinus(next.replace(/[^0-9-]/g, '')));
    case 'signedDecimal':
      return stripRedundantLeadingZeros(sanitizeSignedDecimalInput(next));
    case 'percent':
      return stripRedundantLeadingZeros(normalizeLeadingMinus(next.replace(/[^0-9,.\-]/g, '')));
    case 'phone':
      return next.replace(/[^0-9+\s\-()]/g, '');
    default:
      return next;
  }
}

export function bindNumericOnChange(
  onChange: (text: string) => void,
  kind: NumericInputKind
): (text: string) => void {
  return (text) => onChange(sanitizeNumericInputText(text, kind));
}

export function numericTextInputDefaults(kind: NumericInputKind): Pick<
  TextInputProps,
  'keyboardType' | 'inputMode'
> {
  switch (kind) {
    case 'decimalMoney':
    case 'percent':
      return { keyboardType: 'decimal-pad', inputMode: 'decimal' };
    case 'signedDecimal':
      return { keyboardType: 'numeric', inputMode: 'decimal' };
    case 'integer':
    case 'digits':
      return { keyboardType: 'number-pad', inputMode: 'numeric' };
    case 'signedInteger':
      return { keyboardType: 'numeric', inputMode: 'numeric' };
    case 'phone':
      return { keyboardType: 'phone-pad', inputMode: 'tel' };
    default:
      return { keyboardType: 'default' };
  }
}
